import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Dropdown, Image, Badge } from "react-bootstrap";
import i18n from "../../i18n";
import { logoutUser } from "../../redux/slices/authSlice";
import AgilityLogo from "../../assets/images/agility-semfundo.png";
import { getProfilePictureUrl } from '../../utils/imageUtils';
import UserInitialsAvatar from '../../utils/userInitialAvatar';
import { ROLES } from "../../utils/constants/roles";

import "./Sidebar.css";

import { Sun, Moon } from "lucide-react";
import { useTheme } from "../../hooks/useTheme";

import {
  FaHome,
  FaBusinessTime,
  FaRegClock,
  FaUmbrellaBeach,
  FaClipboardCheck,
  FaRegFileAlt,
  FaCalendarAlt,
  FaUsers,
  FaArrowLeft,
  FaArrowRight,
  FaChevronDown,
  FaChevronRight,
  FaCog,
  FaBell,
  FaRegBell,
  FaSignOutAlt,
  FaUser,
  FaSearch,
  FaChartPie
} from "react-icons/fa";

const ORGANIZATION_MANAGER_ROLES = [ROLES.SUPER_ADMIN, ROLES.MANAGER];

const Sidebar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeItem, setActiveItem] = useState("/dashboard");
  const [isDropdownPersonalOpen, setIsDropdownPersonalOpen] = useState(true);
  const [isDropdownEnterpriseOpen, setIsDropdownEnterpriseOpen] = useState(true);
  const [isDropdownSystemOpen, setIsDropdownSystemOpen] = useState(true);

  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const canManageOrganization = ORGANIZATION_MANAGER_ROLES.includes(user?.role?.name);

  const [notifications, setNotifications] = useState([]);

  const { theme, toggleTheme } = useTheme();
  const themeLabel = theme === "dark" ? i18n.t("components.Sidebar.lightMode") : i18n.t("components.Sidebar.darkMode");

  useEffect(() => {
    if (isAuthenticated) setActiveItem("/dashboard");
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;

  const toggleSidebar = () => setIsCollapsed(!isCollapsed);
  const handleItemClick = (item) => { setActiveItem(item); navigate(`/${item}`); };
  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const markAsRead = (id) => setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className={`atlar-sidebar custom-scrollbar ${isCollapsed ? "collapsed" : ""}`} style={{ zIndex: 9999 }}>

      <div className="sidebar-top-actions d-flex justify-content-between align-items-center p-3 border-bottom">
        <button className="sidebar-toggle-btn border-0 bg-transparent txt-main" onClick={toggleSidebar}>
          {isCollapsed ? <FaArrowRight size={15} /> : <FaArrowLeft size={15} />}
        </button>

        <Dropdown
          align={isCollapsed ? "start" : "end"}
          className="notification-dropdown"
          onToggle={(isOpen) => setIsNotifOpen(isOpen)}
        >
          <Dropdown.Toggle variant="transparent" className="border-0 p-0 txt-main position-relative shadow-none d-flex align-items-center no-caret">
            <FaBell size={20} />
            {unreadCount > 0 && (
              <Badge bg="danger" pill className="position-absolute top-0 start-100 translate-middle" style={{ fontSize: "0.55rem" }}>
                {unreadCount}
              </Badge>
            )}
            <FaChevronDown
              size={12}
              className="ms-1 txt-subtle sidebar-animated-icon"
              style={{ transform: isNotifOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          </Dropdown.Toggle>

          <Dropdown.Menu className="shadow-lg rounded-3 border-0" style={{ width: "280px", zIndex: 1050 }}>
            <div className="px-3 py-2 border-bottom fw-bold text-center txt-main">{i18n.t("components.Sidebar.notifications")}</div>
            
            {notifications.length > 0 ? (
              notifications.slice(0, 3).map((notif) => (
                <Dropdown.Item key={notif.id} onClick={() => markAsRead(notif.id)} className="d-flex align-items-center px-2 py-2 border-bottom">
                  <Image src={notif.photo} roundedCircle width={32} height={32} className="me-2" />
                  <div style={{ fontSize: "0.8rem", whiteSpace: "normal" }} className="txt-main">
                    <strong>{notif.sender}</strong>: {notif.message}
                  </div>
                  {!notif.read && <div className="rounded-circle ms-auto bg-main badge-indicator"></div>}
                </Dropdown.Item>
              ))
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center py-5 px-3 text-center">
                <FaRegBell size={32} className="mb-3 txt-subtle" style={{ opacity: 0.3 }} />
                <span className="txt-subtle fw-semibold" style={{ fontSize: "0.9rem" }}>
                  {i18n.t("components.Sidebar.noNotifications", "Sem notificações no momento.")}
                </span>
              </div>
            )}


            <div className="text-center p-2 border-top">
              <button 
                className="w-100 py-2 rounded border-0 fw-bold transition-all" 
                style={{ 
                  backgroundColor: "var(--brand-primary)",
                  color: "var(--white, #0d6efd)", 
                  fontSize: "0.85rem" 
                }}
              >
                {i18n.t("components.Sidebar.viewAll")}
              </button>
            </div>
          </Dropdown.Menu>
        </Dropdown>

        <button
          type="button"
          className="sidebar-toggle-btn border-0 bg-transparent txt-subtle ms-auto"
          onClick={toggleTheme}
          aria-label={themeLabel}
          title={themeLabel}
        >
          {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </div>

      <div className="d-flex flex-column pt-3 px-2 flex-grow-1">

        <button className={`sidebar-btn ${activeItem === "dashboard" || activeItem === "/dashboard" ? "active" : ""}`} onClick={() => handleItemClick("dashboard")}>
          <FaHome className="sidebar-icon" />
          <span className="hide-on-collapse fw-bold">{i18n.t("components.Sidebar.home")}</span>
        </button>

        <button className="sidebar-category-btn mt-3" onClick={() => setIsDropdownPersonalOpen(!isDropdownPersonalOpen)}>
          <span className="hide-on-collapse txt-subtle fw-bold">{i18n.t("components.Sidebar.personal")}</span>
          {!isCollapsed && (isDropdownPersonalOpen ? <FaChevronDown className="ms-auto txt-subtle" size={10} /> : <FaChevronRight className="ms-auto txt-subtle" size={10} />)}
        </button>
        {isDropdownPersonalOpen && (
          <div className="sidebar-dropdown">
            <button
              className={`sidebar-btn ${activeItem === "profile" ? "active" : ""}`}
              onClick={() => {
                setActiveItem("profile");
                navigate(`/organization/employee/${user?.id}`);
              }}
            >
              <FaUser className="sidebar-icon" /> <span className="hide-on-collapse">{i18n.t("components.Sidebar.profile", "Perfil")}</span>
            </button>
            <button className={`sidebar-btn ${activeItem === "clock" ? "active" : ""}`} onClick={() => handleItemClick("clock")}>
              <FaRegClock className="sidebar-icon" /> <span className="hide-on-collapse">{i18n.t("components.Sidebar.pointRegister")}</span>
            </button>
            <button className={`sidebar-btn ${activeItem === "user-shifts" ? "active" : ""}`} onClick={() => handleItemClick("user-shifts")}>
              <FaBusinessTime className="sidebar-icon" /> <span className="hide-on-collapse">{i18n.t("components.Sidebar.shift")}</span>
            </button>
            <button className={`sidebar-btn ${activeItem === "absences" ? "active" : ""}`} onClick={() => handleItemClick("absences")}>
              <FaUmbrellaBeach className="sidebar-icon" /> <span className="hide-on-collapse">{i18n.t("components.Sidebar.absences")}</span>
            </button>
          </div>
        )}

        <button className="sidebar-category-btn mt-3" onClick={() => setIsDropdownEnterpriseOpen(!isDropdownEnterpriseOpen)}>
          <span className="hide-on-collapse txt-subtle fw-bold">{i18n.t("components.Sidebar.enterprise")}</span>
          {!isCollapsed && (isDropdownEnterpriseOpen ? <FaChevronDown className="ms-auto txt-subtle" size={10} /> : <FaChevronRight className="ms-auto txt-subtle" size={10} />)}
        </button>
        {isDropdownEnterpriseOpen && (
          <div className="sidebar-dropdown">
            <button className={`sidebar-btn ${activeItem === "organization" ? "active" : ""}`} onClick={() => handleItemClick("organization")}>
              <FaUsers className="sidebar-icon" /> <span className="hide-on-collapse">{i18n.t("components.Sidebar.organization")}</span>
            </button>
            <button className={`sidebar-btn ${activeItem === "calendar" ? "active" : ""}`} onClick={() => handleItemClick("calendar")}>
              <FaCalendarAlt className="sidebar-icon" /> <span className="hide-on-collapse">{i18n.t("components.Sidebar.calendar")}</span>
            </button>
            <button className={`sidebar-btn ${activeItem === "documents" ? "active" : ""}`} onClick={() => handleItemClick("documents")}>
              <FaRegFileAlt className="sidebar-icon" /> <span className="hide-on-collapse">{i18n.t("components.Sidebar.documents")}</span>
            </button>
            
            <button className={`sidebar-btn ${activeItem === "projects" ? "active" : ""}`} onClick={() => handleItemClick("projects")}>
              <FaClipboardCheck className="sidebar-icon" /> <span className="hide-on-collapse">{i18n.t("components.Sidebar.projects")}</span>
            </button>

            <button className={`sidebar-btn ${activeItem === "time-distribution" ? "active" : ""}`} onClick={() => handleItemClick("time-distribution")}>
              <FaChartPie className="sidebar-icon" /> <span className="hide-on-collapse">{i18n.t("components.Sidebar.timeDistribution", "Distribuição de horas")}</span>
            </button>

          </div>
        )}

        {canManageOrganization && (
          <>
            <button className="sidebar-category-btn mt-3" onClick={() => setIsDropdownSystemOpen(!isDropdownSystemOpen)}>
              <span className="hide-on-collapse txt-subtle fw-bold">{i18n.t("components.Sidebar.systemConfiguration")}</span>
              {!isCollapsed && (isDropdownSystemOpen ? <FaChevronDown className="ms-auto txt-subtle" size={10} /> : <FaChevronRight className="ms-auto txt-subtle" size={10} />)}
            </button>
            {isDropdownSystemOpen && (
              <div className="sidebar-dropdown">
                <button className={`sidebar-btn ${activeItem === "organization/settings" ? "active" : ""}`} onClick={() => handleItemClick("organization/settings")}>
                  <FaCog className="sidebar-icon" /> <span className="hide-on-collapse">{i18n.t("components.Sidebar.organizationSettings")}</span>
                </button>
              </div>
            )}
          </>
        )}

      </div>

      <div className="sidebar-footer mt-auto border-top pt-3 pb-3">

        <div className="px-3 mb-3">
          {isCollapsed ? (
            <div className="sidebar-search-icon-box">
              <FaSearch className="txt-subtle" size={14} />
            </div>
          ) : (
            <div className="position-relative">
              <FaSearch className="position-absolute txt-subtle sidebar-search-icon" size={14} />
              <input
                type="text"
                placeholder={i18n.t("components.Sidebar.search")}
                className="w-100 border-0 outline-none txt-main sidebar-search-input"
              />
            </div>
          )}
        </div>

        <Dropdown
          drop="up"
          className="w-100 px-2 mb-1"
          onToggle={(isOpen) => setIsProfileOpen(isOpen)}
        >
          <Dropdown.Toggle
            variant="transparent"
            className="w-100 d-flex align-items-center border-0 p-1 rounded shadow-none bg-transparent no-caret hover-bg-dark"
          >
            {user?.profile_picture ? (
                <Image
                    src={getProfilePictureUrl(user)}
                    roundedCircle
                    width={36}
                    height={36}
                    className="sidebar-profile-img"
                    style={{ objectFit: 'cover', flexShrink: 0 }}
                />
            ) : (
                <UserInitialsAvatar 
                    first_name={user?.first_name || ''} 
                    last_name={user?.last_name || ''} 
                    style={{ 
                        width: '36px', 
                        height: '36px', 
                        fontSize: '0.85rem',
                        flexShrink: 0       
                    }} 
                />
            )}
            {!isCollapsed && (
              <>
                <div className="ms-2 text-start flex-grow-1 overflow-hidden">
                  <div className="fw-bold text-truncate txt-main" style={{ fontSize: "0.85rem" }}>
                    {user ? `${user.first_name} ${user.last_name}` : i18n.t("components.Sidebar.defaultUser")}
                  </div>
                  <div className="txt-subtle text-truncate" style={{ fontSize: "0.75rem" }}>
                    {i18n.t("components.Sidebar.viewProfile")}
                  </div>
                </div>
                <FaChevronDown
                  size={12}
                  className="txt-subtle ms-2 sidebar-animated-icon"
                  style={{ transform: isProfileOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                />
              </>
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu className="shadow-lg rounded-3 mb-2 border-0" style={{ zIndex: 1050 }}>
            <Dropdown.Item onClick={() => navigate("/profile")} className="py-2 txt-main">
              <FaUser className="me-2 txt-subtle" /> {i18n.t("components.Sidebar.settingsMyProfile")}
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={handleLogout} className="py-2 txt-danger fw-bold">
              <FaSignOutAlt className="me-2" /> {i18n.t("components.Sidebar.logout")}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        {!isCollapsed && (
          <div className="text-center">
            <img src={AgilityLogo} alt="Logo AgilityHR" className="sidebar-agility-logo" />
          </div>
        )}
      </div>

    </div>
  );
};

export default Sidebar;
